import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { fetchFleetOverview } from '../api/equipment';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import DeviceTable from '../components/equipment/DeviceTable';
import FleetSummary from '../components/equipment/FleetSummary';
import SchoolEquipmentRow from '../components/equipment/SchoolEquipmentRow';
import { DEVICE_TYPES, schoolHasProblem } from '../components/equipment/stateMeta';
import { SegmentedControl, surface } from '../components/ui/surface';

const control =
  'h-9 rounded-xl bg-white/[0.04] px-3 text-sm text-muted-100 placeholder:text-muted-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400';
const filterButton = (active) =>
  `inline-flex h-9 items-center rounded-xl px-3 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
    active
      ? 'bg-white/[0.1] text-white'
      : 'bg-white/[0.04] text-muted-300 hover:bg-white/[0.08] hover:text-white'
  }`;
const actionButton =
  'inline-flex items-center gap-1.5 rounded-xl bg-white/[0.04] px-3 py-2 text-xs font-medium text-muted-300 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400';

const Equipment = ({ userEmail, userRole, onLogout }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSidebarVisible, setSidebarVisible] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [view, setView] = useState('schools');
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('');
  const typeFromUrl = searchParams.get('type');
  const [type, setType] = useState(() =>
    DEVICE_TYPES.includes(typeFromUrl) ? typeFromUrl : ''
  );
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [showNotEquipped, setShowNotEquipped] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const next = searchParams.get('type');
    setType(DEVICE_TYPES.includes(next) ? next : '');
  }, [searchParams]);

  const setTypeFilter = useCallback((value) => {
    setType(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('type', value);
      else next.delete('type');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setOverview(await fetchFleetOverview());
    } catch (err) {
      setError(err?.response?.data?.message || t('equipment.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Автообновления нет сознательно, но вернувшийся к вкладке человек не должен
  // смотреть на утренние числа, считая их сегодняшними.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const schools = overview?.schools || [];

  const regions = useMemo(
    () => [...new Set(schools.map((s) => s.region).filter(Boolean))].sort(),
    [schools]
  );

  const filteredSchools = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return schools.filter((school) => {
      const equipped = (school.devices?.length || 0) > 0;
      if (!equipped && !showNotEquipped) return false;
      if (region && school.region !== region) return false;
      if (type && !(school.byType?.[type]?.total > 0)) return false;
      if (onlyProblems && !schoolHasProblem(school)) return false;
      if (needle) {
        const haystack = `${school.name} ${school.region || ''} ${school.city || ''} ${school.routerIp || ''}`;
        if (!haystack.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [schools, search, region, type, onlyProblems, showNotEquipped]);

  const filteredDevices = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return filteredSchools.flatMap((school) =>
      (school.devices || [])
        .filter((device) => !type || device.deviceType === type)
        .filter((device) => {
          if (!onlyProblems) return true;
          return device.connectivity === 'unavailable' || device.readiness?.state === 'not_ready';
        })
        .filter((device) => {
          if (!needle) return true;
          const haystack = `${device.name || ''} ${device.target || ''} ${device.model || ''} ${school.name}`;
          return haystack.toLowerCase().includes(needle);
        })
        .map((device) => ({
          ...device,
          schoolName: school.name,
          region: school.region,
          routerIp: school.routerIp,
        }))
    );
  }, [filteredSchools, search, type, onlyProblems]);

  const exportExcel = useCallback(() => {
    if (!overview) return;

    const rows = schools.flatMap((school) =>
      (school.devices || []).map((device) => ({
        [t('equipment.export.columns.school')]: school.name,
        [t('equipment.export.columns.region')]: school.region || '',
        [t('equipment.export.columns.city')]: school.city || '',
        [t('equipment.export.columns.routerIp')]: school.routerIp || '',
        [t('equipment.export.columns.schoolChannel')]: t(`equipment.channel.${school.channel}`),
        [t('equipment.export.columns.type')]: t(`equipment.typeSingular.${device.deviceType}`),
        [t('equipment.export.columns.name')]: device.name || `#${device.id}`,
        [t('equipment.export.columns.address')]: device.target || '',
        [t('equipment.export.columns.model')]: device.model || '',
        [t('equipment.export.columns.connectivity')]: t(`equipment.state.${device.connectivity}`),
        [t('equipment.export.columns.readiness')]: t(`equipment.readiness.${device.readiness?.state || 'unknown'}`),
        [t('equipment.export.columns.reasons')]: (device.readiness?.reasons || [])
          .map((reason) => t(`equipment.reasons.${reason.reason}`, { count: reason.count }))
          .join('; '),
      }))
    );

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 28 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 16 },
      { wch: 18 }, { wch: 24 }, { wch: 22 }, { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 36 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('equipment.export.sheetName'));

    const date = new Date()
      .toLocaleDateString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit' })
      .replace(/\//g, '-');
    XLSX.writeFile(wb, t('equipment.export.fileName', { date }));
  }, [overview, schools, t]);

  const snapshotAt = overview?.snapshotAt ? new Date(overview.snapshotAt) : null;

  return (
    <div className="relative flex min-h-screen flex-col bg-surface-base text-muted-100">
      <Header
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={() => setSidebarVisible((prev) => !prev)}
        userEmail={userEmail}
        userRole={userRole}
      />
      <div className="flex flex-1">
        <Sidebar
          onLogout={onLogout}
          userRole={userRole}
          isSidebarVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
        />
        <main className="relative flex min-w-0 flex-1 flex-col gap-4 bg-surface-base/60 px-3 py-4 sm:gap-6 sm:px-4 sm:py-6 lg:px-10">
            <header className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="font-display text-xl text-white md:text-2xl">{t('equipment.title')}</h1>
                <p className="mt-1 text-sm text-muted-400">{t('equipment.subtitle')}</p>
              </div>
              <div className="flex items-center gap-3">
                {snapshotAt && (
                  <span className="text-xs text-muted-500">
                    {t('equipment.snapshotAt', { time: snapshotAt.toLocaleTimeString() })}
                  </span>
                )}
                <button
                  type="button"
                  onClick={exportExcel}
                  disabled={!overview || loading}
                  className={actionButton}
                >
                  <span className="material-icons text-base">download</span>
                  {t('equipment.export.button')}
                </button>
                <button
                  type="button"
                  onClick={load}
                  disabled={loading}
                  className={actionButton}
                >
                  <span className={`material-icons text-base ${loading ? 'animate-spin' : ''}`}>refresh</span>
                  {t('equipment.refresh')}
                </button>
              </div>
            </header>

            {error && (
              <p className="rounded-3xl bg-critical/10 px-6 py-4 text-sm text-critical">
                {error}
              </p>
            )}

            {overview?.degraded && (
              <p className="rounded-3xl bg-warning/10 px-6 py-4 text-sm text-warning">
                {t('equipment.degraded')}
              </p>
            )}

            {overview && (
              <FleetSummary
                overview={overview}
                activeType={type}
                showNotEquipped={showNotEquipped}
                filtersCleared={
                  !type
                  && !onlyProblems
                  && !showNotEquipped
                  && !region
                  && !search.trim()
                }
                onResetFilters={() => {
                  setSearch('');
                  setRegion('');
                  setTypeFilter('');
                  setOnlyProblems(false);
                  setShowNotEquipped(false);
                }}
                onFilterType={(nextType) => {
                  setTypeFilter(nextType);
                  setShowNotEquipped(false);
                  setOnlyProblems(false);
                }}
                onShowNotEquipped={() => {
                  setShowNotEquipped(true);
                  setOnlyProblems(false);
                  setTypeFilter('');
                }}
              />
            )}

            <section className={`rounded-3xl ${surface.section} p-4 sm:p-6`}>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <SegmentedControl
                  options={['schools', 'devices'].map((mode) => ({
                    id: mode,
                    label: t(`equipment.view.${mode}`),
                  }))}
                  value={view}
                  onChange={setView}
                />

                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('equipment.filters.search')}
                  className={`${control} min-w-[200px] flex-1`}
                />

                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  className={control}
                >
                  <option value="">{t('equipment.filters.allRegions')}</option>
                  {regions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>

                <select
                  value={type}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className={control}
                >
                  <option value="">{t('equipment.filters.allTypes')}</option>
                  {DEVICE_TYPES.map((value) => (
                    <option key={value} value={value}>{t(`equipment.types.${value}`)}</option>
                  ))}
                </select>

                <button
                  type="button"
                  aria-pressed={onlyProblems}
                  onClick={() => setOnlyProblems((prev) => !prev)}
                  className={filterButton(onlyProblems)}
                >
                  {t('equipment.filters.onlyProblems')}
                </button>

                {view === 'schools' && (
                  <button
                    type="button"
                    aria-pressed={showNotEquipped}
                    onClick={() => setShowNotEquipped((prev) => !prev)}
                    className={filterButton(showNotEquipped)}
                  >
                    {t('equipment.filters.showNotEquipped')}
                  </button>
                )}
              </div>

              {loading && !overview ? (
                <p className="py-6 text-center text-sm text-muted-500">{t('common.loading')}</p>
              ) : view === 'schools' ? (
                <div className="flex flex-col gap-2">
                  {filteredSchools.length === 0 && !loading ? (
                    <p className="py-6 text-center text-sm text-muted-500">{t('equipment.empty.schools')}</p>
                  ) : (
                    filteredSchools.map((school) => (
                      <SchoolEquipmentRow
                        key={school.objectId}
                        school={school}
                        expanded={expandedId === school.objectId}
                        onToggle={() =>
                          setExpandedId((prev) => (prev === school.objectId ? null : school.objectId))
                        }
                      />
                    ))
                  )}
                </div>
              ) : (
                <DeviceTable devices={filteredDevices} />
              )}
            </section>
        </main>
      </div>
    </div>
  );
};

export default Equipment;
